import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTrainingInitLazyQuery } from '~/data';
import { client } from '~/data/client';
import { select } from '~/lib/select';
import { markAppReload } from '~/lib/reloadDiagnostics';
import { getLmsAdapter, logLmsLanguage } from '~/lms';
import { NotFound } from '~/ui/not-found/NotFound';
import { Spinner } from '~/ui/spinner/Spinner';
import { useTrainingParams } from '../useTrainingParams';
import { processData } from './processData';
import { syncLearnerLanguageFromLms } from './syncLearnerLanguageFromLms';
import { TrainingState } from './TrainingState';
import { useTrainingStore } from './useTrainingStore';

const selector = (s: TrainingState) =>
  select(s, 'textLocale', 'mediumLocale', 'subtitleLocale');

function localesMismatch(
  remote: {
    textLocale?: string | null;
    mediumLocale?: string | null;
    subtitleLocale?: string | null;
  },
  local: { textLocale: string; mediumLocale: string; subtitleLocale: string }
) {
  return (
    remote.textLocale != local.textLocale ||
    remote.mediumLocale != local.mediumLocale ||
    remote.subtitleLocale != local.subtitleLocale
  );
}

export const InitTrainingState: FC = ({ children }) => {
  const { learnerTrainingId } = useTrainingParams();
  const { pathname } = useLocation();
  const isOnboarding = /\/onboarding\/?$/.test(pathname);
  const [getTrainingInit, { data, refetch, error }] =
    useTrainingInitLazyQuery();
  const [done, setDone] = useState(false);
  const lmsSyncedForTrainingRef = useRef<string | null>(null);
  const initDataAppliedForTrainingRef = useRef<string | null>(null);
  const launchSyncGenerationRef = useRef(0);
  const lmsSyncInProgressRef = useRef(false);
  const localeRefreshInProgressRef = useRef(false);
  const localeRefreshGenerationRef = useRef(0);
  const cancelLocaleRefreshRef = useRef<(() => void) | null>(null);
  const { textLocale, mediumLocale, subtitleLocale } =
    useTrainingStore(selector);

  useEffect(() => {
    setDone(false);
    lmsSyncedForTrainingRef.current = null;
    initDataAppliedForTrainingRef.current = null;
    launchSyncGenerationRef.current += 1;
    localeRefreshGenerationRef.current += 1;
    localeRefreshInProgressRef.current = false;
  }, [learnerTrainingId]);

  // Browser Back can return to onboarding while a persisted locale refresh is
  // pending. Keep that result; cancel only when locales/training change or this
  // initializer unmounts.
  useEffect(
    () => () => cancelLocaleRefreshRef.current?.(),
    [learnerTrainingId, textLocale, mediumLocale, subtitleLocale]
  );

  useEffect(() => {
    // Soft-refresh course data when a locale setting changes (mutation already
    // updated the store). Skip onboarding: LMS/browser auto-detect can update
    // `textLocale` locally before the server catches up. Recheck when leaving
    // onboarding so a locale saved there refreshes the outline and content.

    if (!refetch || !data?.learnerTraining || isOnboarding) return;
    if (lmsSyncInProgressRef.current) return;
    if (
      !localesMismatch(data.learnerTraining, {
        textLocale,
        mediumLocale,
        subtitleLocale
      })
    ) {
      return;
    }

    const generation = ++localeRefreshGenerationRef.current;
    let cancelled = false;
    cancelLocaleRefreshRef.current = () => {
      cancelled = true;
    };

    void (async () => {
      localeRefreshInProgressRef.current = true;
      setDone(false);

      const isCurrent = () =>
        !cancelled && generation === localeRefreshGenerationRef.current;

      const fallbackReload = () => {
        // Soft-refresh could not apply new locale data; hard reload so we do
        // not leave store locales out of sync with course content.
        markAppReload('locale-soft-refresh-failed');
        document.location.reload();
      };

      try {
        const result = await refetch();
        if (!isCurrent()) return;

        if (!result.data) {
          fallbackReload();
          return;
        }

        useTrainingStore.setState(processData(result.data));

        // SequenceDetails is keyed only by training + chapterSequence — same
        // variables return different localized payloads after a locale change.
        // Drop cached sequences so the remounted player refetches fresh content.
        client.cache.evict({ fieldName: 'sequence' });
        client.cache.gc();
      } catch {
        if (isCurrent()) {
          fallbackReload();
        }
      } finally {
        if (generation === localeRefreshGenerationRef.current) {
          localeRefreshInProgressRef.current = false;
          // Always clear the spinner when this generation is still current —
          // even if cancelled — so a bail-early successor cannot leave done=false.
          setDone(true);
        }
      }
    })();
  }, [textLocale, mediumLocale, subtitleLocale, isOnboarding]);

  // Call the API when the learner training id is present or updated
  useEffect(() => {
    if (!learnerTrainingId) return;
    getTrainingInit({ variables: { learnerTrainingUuid: learnerTrainingId } });
  }, [learnerTrainingId]);

  // Process the API data and update the store when data changes
  useEffect(() => {
    let cancelled = false;
    const generation = ++launchSyncGenerationRef.current;

    if (!data || !learnerTrainingId) return;
    if (data.learnerTraining.id !== learnerTrainingId) return;

    if (lmsSyncedForTrainingRef.current === learnerTrainingId) {
      // Locale soft-refresh owns the spinner until cache eviction finishes;
      // do not flip `done` back early when refetch updates `data`.
      if (!localeRefreshInProgressRef.current) {
        setDone(true);
      }
      return;
    }

    // Seed store once per training from the initial query. Re-runs while LMS
    // sync is in flight must not overwrite locales sync may have just persisted.
    if (initDataAppliedForTrainingRef.current !== learnerTrainingId) {
      useTrainingStore.setState(processData(data));
      initDataAppliedForTrainingRef.current = learnerTrainingId;
    }

    void (async () => {
      lmsSyncInProgressRef.current = true;
      try {
        const lms = getLmsAdapter();
        logLmsLanguage('init-training launch sync', {
          adapter: lms.name,
          isAvailable: lms.isAvailable,
          learnerTrainingId
        });

        if (lms.isAvailable) {
          const synced = await syncLearnerLanguageFromLms(lms);
          if (cancelled || generation !== launchSyncGenerationRef.current) {
            return;
          }

          if (synced && refetch) {
            const result = await refetch();
            if (cancelled || generation !== launchSyncGenerationRef.current) {
              return;
            }

            if (result.data?.learnerTraining.id === learnerTrainingId) {
              useTrainingStore.setState(processData(result.data));
            }
          }
        }

        if (!cancelled && generation === launchSyncGenerationRef.current) {
          lmsSyncedForTrainingRef.current = learnerTrainingId;
        }
      } catch (error) {
        logLmsLanguage('init-training launch sync failed', {
          error: error instanceof Error ? error.message : String(error)
        });
      } finally {
        if (generation === launchSyncGenerationRef.current) {
          lmsSyncInProgressRef.current = false;
        }
      }

      if (!cancelled && generation === launchSyncGenerationRef.current) {
        setDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, learnerTrainingId, refetch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statusCode = (error?.networkError as any)?.statusCode;
  if (statusCode === 401 || statusCode === 404) return <NotFound />;

  if (!done) return <Spinner />;

  if (!children) return null;

  return <>{children}</>;
};
